import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import client, { API, formatApiError } from "@/api/client";
import Layout from "@/components/Layout";
import Watermark from "@/components/Watermark";
import { useAuth } from "@/context/AuthContext";
import { Bookmark, Check, Download, MessageCircle, StickyNote, ChevronLeft, Award, ImagePlus } from "lucide-react";
import { toast, Toaster } from "sonner";

// Lessons that explicitly reserve a chart slot (admin uploads via Content tab).
const IMAGE_PLACEHOLDERS = {
  "L3-fvg-ifvg-cisd-l1": "IFVG — Breach & Retest",
  "L3-fvg-ifvg-cisd-l2": "CISD — Line Firing on the LTF",
  "L3-liquidity-mapping-l1": "External Liquidity — Weekly H/L, PDH/PDL, EH/EL, SSL/BSL",
  "L3-liquidity-mapping-l2": "NWOG · Data Wicks · LRL",
};

export default function LessonView() {
  const { moduleId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [mod, setMod] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [active, setActive] = useState(0);
  const [notes, setNotes] = useState("");
  const [quiz, setQuiz] = useState({ open: false, answers: [], result: null });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await client.get(`/modules/${moduleId}`);
      setMod(data.module); setLessons(data.lessons);
      if (data.quiz_result) setQuiz(q => ({ ...q, result: data.quiz_result }));
      setNotes(data.lessons[0]?.notes || "");
    } catch (e) {
      if (e.response?.status === 423) {
        toast.error(formatApiError(e));
        setTimeout(() => nav("/dashboard", { replace: true }), 1200);
      } else {
        toast.error(formatApiError(e));
      }
    }
  }, [moduleId, nav]);

  const loadComments = useCallback(async (lessonId) => {
    if (!lessonId) return;
    const { data } = await client.get(`/lessons/${lessonId}/comments`);
    setComments(data.comments);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (lessons[active]) {
      setNotes(lessons[active].notes || "");
      loadComments(lessons[active].id);
    }
  }, [active, lessons, loadComments]);

  // Anti-sharing: block right-click, copy/cut, print, save shortcuts, F12 devtools open.
  // We deliberately allow Ctrl+A/C/X while typing inside a textarea/input so notes & Q&A stay usable.
  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === "TEXTAREA" || tag === "INPUT" || el.isContentEditable;
    };
    const offCtx = (e) => { if (!isEditable(e.target)) e.preventDefault(); };
    const offCopy = (e) => {
      if (isEditable(e.target)) return;
      e.preventDefault();
      toast.error("Copying is disabled in The Boardroom", { id: "copy-blocked" });
    };
    const offCut = (e) => { if (!isEditable(e.target)) e.preventDefault(); };
    const offKey = (e) => {
      const k = e.key?.toLowerCase();
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      const inField = isEditable(e.target);
      // Print/Save are always blocked
      if (k === "p") {
        e.preventDefault();
        toast.error("Printing is disabled — use Download Module PDF", { id: "print-blocked" });
        return;
      }
      if (k === "s") {
        e.preventDefault();
        toast.error("Saving pages is disabled", { id: "save-blocked" });
        return;
      }
      if (k === "u") { // view-source
        e.preventDefault();
        return;
      }
      // Copy / Cut / Select-All only blocked outside editable fields
      if (!inField && ["c", "x", "a"].includes(k)) {
        e.preventDefault();
        if (k === "c" || k === "x") {
          toast.error("Copying is disabled in The Boardroom", { id: "copy-blocked" });
        }
      }
    };
    const beforePrint = () => {
      window.stop?.();
    };
    document.addEventListener("contextmenu", offCtx);
    document.addEventListener("copy", offCopy);
    document.addEventListener("cut", offCut);
    document.addEventListener("keydown", offKey);
    window.addEventListener("beforeprint", beforePrint);
    document.body.classList.add("boardroom-lesson-mode");
    return () => {
      document.removeEventListener("contextmenu", offCtx);
      document.removeEventListener("copy", offCopy);
      document.removeEventListener("cut", offCut);
      document.removeEventListener("keydown", offKey);
      window.removeEventListener("beforeprint", beforePrint);
      document.body.classList.remove("boardroom-lesson-mode");
    };
  }, []);

  const current = lessons[active];

  const complete = async () => {
    if (!current) return;
    await client.post("/progress/lesson-complete", { lesson_id: current.id });
    toast.success("Lesson marked complete");
    load();
    if (active < lessons.length - 1) setActive(active + 1);
  };

  const toggleBookmark = async () => {
    const { data } = await client.post(`/lessons/${current.id}/bookmark`);
    toast(data.bookmarked ? "Bookmarked" : "Bookmark removed");
    load();
  };

  const saveNotes = async () => {
    await client.post(`/lessons/${current.id}/notes`, { notes });
    toast.success("Notes saved");
  };

  const submitQuiz = async () => {
    const { data } = await client.post(`/quiz/${moduleId}/submit`, { answers: quiz.answers });
    toast.success(`Quiz scored ${data.score}%`);
    await load();
    // Re-apply fresh details (load() replaced result with the DB copy that has no details)
    setQuiz(q => ({ ...q, result: data }));
  };

  const downloadPdf = async () => {
    const r = await client.get(`/modules/${moduleId}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `boardroom-${mod.slug}.pdf`; a.click();
    URL.revokeObjectURL(url);
    toast.success("PDF downloaded — watermarked with your name");
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    await client.post(`/lessons/${current.id}/comments`, { body: newComment });
    setNewComment("");
    loadComments(current.id);
  };

  if (!mod) return <Layout><p className="text-neutral-500">Loading…</p></Layout>;

  return (
    <>
      <Watermark />
      <Toaster theme="dark" position="top-right" />
      <Layout>
        <div className="mb-8">
          <Link to="/dashboard" className="text-xs uppercase tracking-widest text-neutral-500 hover:text-[#D4AF37] inline-flex items-center gap-1" data-testid="back-dashboard">
            <ChevronLeft size={14} /> Back to Curriculum
          </Link>
        </div>
        <div className="grid lg:grid-cols-[260px_1fr] gap-6 md:gap-10">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 self-start" data-testid="lesson-sidebar">
            <p className="overline">Level {mod.level}</p>
            <h1 className="font-display text-2xl md:text-3xl leading-tight mt-1 mb-4 md:mb-6">{mod.title}</h1>
            <div className="gold-divider mb-4 md:mb-6" />

            {/* Mobile-only: dropdown to jump lesson */}
            <div className="lg:hidden mb-4">
              <label className="overline mb-2 block">Jump to lesson</label>
              <select
                value={quiz.open ? "__quiz__" : String(active)}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__quiz__") {
                    setQuiz({ open: true, answers: new Array(mod.quiz?.length || 0).fill(-1), result: null });
                  } else {
                    setQuiz(q => ({ ...q, open: false }));
                    setActive(parseInt(v, 10));
                  }
                }}
                data-testid="lesson-mobile-select"
                className="input-boardroom font-body">
                {lessons.map((l, i) => (
                  <option key={l.id} value={i}>
                    {String(i + 1).padStart(2, "0")} · {l.title}{l.completed ? " ✓" : ""}
                  </option>
                ))}
                <option value="__quiz__">◇ Module Quiz{quiz.result ? ` · ${quiz.result.score}%` : ""}</option>
              </select>

              {/* Prominent Module Quiz button — matches desktop sidebar */}
              <button
                data-testid="quiz-tab-mobile"
                onClick={() => setQuiz({ open: true, answers: new Array(mod.quiz?.length || 0).fill(-1), result: null })}
                className={`mt-4 w-full text-left border pl-4 pr-3 py-3 uppercase tracking-widest text-[11px] transition ${
                  quiz.open
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/5"
                }`}>
                ◇ Module Quiz
                {quiz.result && <span className="text-neutral-400 normal-case tracking-normal ml-2">· {quiz.result.score}%</span>}
              </button>
            </div>

            {/* Desktop-only: full list */}
            <ol className="hidden lg:block space-y-3">
              {lessons.map((l, i) => (
                <li key={l.id}>
                  <button data-testid={`lesson-item-${i}`} onClick={() => setActive(i)}
                    className={`text-left w-full text-sm py-2 border-l-2 pl-4 transition ${
                      i === active ? "border-[#D4AF37] text-white" : "border-[#222] text-neutral-500 hover:text-neutral-200 hover:border-neutral-500"
                    }`}>
                    <span className="font-mono text-[10px] text-neutral-500 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {l.title}
                    {l.completed && <Check size={12} className="inline ml-2 text-[#D4AF37]" />}
                  </button>
                </li>
              ))}
              <li>
                <button data-testid="quiz-tab" onClick={() => setQuiz(q => ({ open: true, answers: new Array(mod.quiz?.length || 0).fill(-1), result: null }))}
                  className="text-left w-full text-sm py-2 border-l-2 pl-4 text-[#D4AF37] border-[#D4AF37]/40 mt-4 uppercase tracking-widest text-[11px]">
                  ◇ Module Quiz {quiz.result && <span className="text-neutral-400">· {quiz.result.score}%</span>}
                </button>
              </li>
            </ol>
          </aside>

          {/* Content */}
          <div className="max-w-3xl no-select">
            {!quiz.open ? (
              <article data-testid="lesson-content">
                <p className="overline">Lesson {active + 1} / {lessons.length}</p>
                <h2 className="font-display text-4xl md:text-5xl mt-2 mb-8 leading-tight">{current.title}</h2>
                <div className="gold-divider mb-8" />
                <div className="prose-boardroom">
                  {current.body.split("\n\n").map((p, i) => (
                    <p key={i} className="text-neutral-200 text-[17px] leading-[1.85] mb-6 font-body">
                      {p.split(/(\*\*[^*]+\*\*)/g).map((chunk, j) =>
                        chunk.startsWith("**") ? <strong key={j} className="text-[#D4AF37] font-semibold">{chunk.slice(2, -2)}</strong> : chunk
                      )}
                    </p>
                  ))}
                </div>

                {current.images && current.images.length > 0 && (
                  <div className="mt-10 space-y-6" data-testid="lesson-images">
                    {current.images.map((img) => (
                      <figure key={img.id} className="border border-[#1f1f1f] bg-[#0a0a0a] p-3">
                        <img
                          src={`${API}/files/${img.id}?auth=${localStorage.getItem("boardroom_token") || ""}`}
                          alt={img.caption || "Chart"}
                          className="w-full h-auto"
                          draggable={false}
                          data-testid={`lesson-image-${img.id}`}
                        />
                        {img.caption && (
                          <figcaption className="mt-3 text-xs uppercase tracking-widest text-[#AA8A25] font-body">
                            ◇ {img.caption}
                          </figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                )}

                {IMAGE_PLACEHOLDERS[current.id] && (!current.images || current.images.length === 0) && (
                  <div className="mt-10" data-testid="lesson-image-placeholder">
                    <figure className="border border-dashed border-[#D4AF37]/40 bg-[#0a0a0a] p-8 text-center">
                      <ImagePlus size={28} className="mx-auto text-[#D4AF37]/70 mb-3" />
                      <p className="font-display text-2xl text-[#D4AF37]">Chart to be added</p>
                      <p className="text-xs uppercase tracking-widest text-[#AA8A25] mt-2">
                        ◇ {IMAGE_PLACEHOLDERS[current.id]}
                      </p>
                      {user?.role === "admin" && (
                        <p className="text-[11px] text-neutral-500 mt-4 font-body">
                          Upload via <span className="text-[#D4AF37]">Admin → Content → {mod.title} → {current.title}</span>
                        </p>
                      )}
                    </figure>
                  </div>
                )}

                <div className="mt-10 flex flex-wrap gap-3">
                  <button onClick={complete} className="btn-gold" data-testid="complete-lesson-btn">
                    <Check size={14} className="inline mr-2" />{current.completed ? "Marked Complete" : "Mark Complete"}
                  </button>
                  <button onClick={toggleBookmark} className="btn-outline-gold" data-testid="bookmark-btn">
                    <Bookmark size={12} className="inline mr-2" fill={current.bookmarked ? "#D4AF37" : "none"} />
                    {current.bookmarked ? "Bookmarked" : "Bookmark"}
                  </button>
                  <button onClick={downloadPdf} className="btn-outline-gold" data-testid="download-pdf-btn">
                    <Download size={12} className="inline mr-2" /> Download Module PDF
                  </button>
                </div>

                {/* Notes */}
                <div className="mt-14 border-t border-[#1f1f1f] pt-8" data-testid="notes-section">
                  <div className="flex items-center gap-2 mb-3">
                    <StickyNote size={14} className="text-[#D4AF37]" />
                    <p className="overline">Your Notes</p>
                  </div>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    onBlur={saveNotes} rows={4}
                    data-testid="notes-textarea"
                    className="input-boardroom font-body resize-none"
                    placeholder="Private notes — only you see these." />
                </div>

                {/* Q&A */}
                <div className="mt-14 border-t border-[#1f1f1f] pt-8" data-testid="qa-section">
                  <div className="flex items-center gap-2 mb-6">
                    <MessageCircle size={14} className="text-[#D4AF37]" />
                    <p className="overline">Q&amp;A · Ask The Boardroom</p>
                  </div>
                  <div className="space-y-4 mb-6">
                    {comments.map(c => (
                      <div key={c.id} className={`border-l-2 pl-4 py-2 ${c.author_role === "admin" ? "border-[#D4AF37]" : "border-[#333]"}`}>
                        <p className="text-xs text-neutral-500 mb-1">
                          <span className={c.author_role === "admin" ? "text-[#D4AF37] font-semibold" : "text-neutral-300"}>
                            {c.author_name}
                          </span>
                          {c.author_role === "admin" && <span className="ml-2 text-[9px] uppercase tracking-widest text-[#D4AF37]">◇ Boardroom</span>}
                          <span className="ml-2 text-neutral-600">{new Date(c.at).toLocaleString()}</span>
                        </p>
                        <p className="text-neutral-200 text-sm font-body">{c.body}</p>
                      </div>
                    ))}
                    {comments.length === 0 && <p className="text-xs text-neutral-600">Be the first to ask a question.</p>}
                  </div>
                  <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)}
                    rows={3} className="input-boardroom font-body resize-none mb-3"
                    data-testid="comment-input"
                    placeholder="Ask a question…" />
                  <button onClick={postComment} className="btn-outline-gold" data-testid="post-comment-btn">Post Question</button>
                </div>
              </article>
            ) : (
              <div data-testid="quiz-panel">
                <p className="overline">Module Quiz</p>
                <h2 className="font-display text-4xl mt-2 mb-8">{mod.title}</h2>
                <div className="gold-divider mb-8" />

                {quiz.result && (
                  <div className={`mb-10 border p-6 ${quiz.result.passed ? "border-[#D4AF37]/50 bg-[#0d0d0d]" : "border-red-900/50 bg-red-950/10"}`}
                       data-testid="quiz-result">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="overline">{quiz.result.passed ? "◇ Passed" : "Not Yet"}</p>
                        <p className={`font-display text-5xl mt-1 ${quiz.result.passed ? "text-[#D4AF37]" : "text-red-300"}`}>
                          {quiz.result.score}%
                        </p>
                        <p className="text-neutral-400 text-sm mt-1">
                          {quiz.result.correct} of {quiz.result.total} correct
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button onClick={() => setQuiz({ open: true, answers: new Array(mod.quiz?.length || 0).fill(-1), result: null })}
                                className="btn-gold" data-testid="retake-quiz-btn">Retake Quiz</button>
                        <button onClick={() => setQuiz({ open: false, answers: [], result: quiz.result })}
                                className="btn-outline-gold">Back to Lessons</button>
                      </div>
                    </div>
                    <p className="text-xs text-neutral-500 mt-4 font-body max-w-xl">
                      {quiz.result.passed
                        ? "You've hit 10/10 — module cleared. Review the lessons any time; the answers stay hidden so the ideas remain yours to work out."
                        : "10/10 is required to pass — any wrong answer resets the round. Review the lessons above, then retake. Correct answers are never revealed."}
                    </p>
                  </div>
                )}

                {(mod.quiz || []).map((q, i) => {
                  const detail = quiz.result?.details?.[i];
                  const answered = quiz.answers[i] !== -1 && quiz.result;
                  const isCorrect = detail?.correct;
                  return (
                  <div key={i} className="mb-8" data-testid={`quiz-q-${i}`}>
                    <div className="flex items-center justify-between mb-4 gap-3">
                      <p className="font-display text-xl flex-1">{i + 1}. {q.q}</p>
                      {answered && (
                        isCorrect ? (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/60 px-2.5 py-1"
                                data-testid={`quiz-q-${i}-verdict-correct`}>
                            <Check size={12} /> Correct
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-400 border border-red-900/70 px-2.5 py-1"
                                data-testid={`quiz-q-${i}-verdict-incorrect`}>
                            ✗ Incorrect
                          </span>
                        )
                      )}
                    </div>
                    <div className="space-y-2">
                      {q.options.map((o, j) => {
                        const chosen = quiz.answers[i] === j;
                        const showResult = quiz.result != null;
                        const chosenIsCorrect = showResult && chosen && isCorrect;
                        const chosenIsWrong = showResult && chosen && !isCorrect;
                        return (
                          <button key={j} data-testid={`quiz-q-${i}-opt-${j}`}
                            onClick={() => {
                              if (quiz.result) return;
                              const a = [...quiz.answers]; a[i] = j; setQuiz({ ...quiz, answers: a });
                            }}
                            className={`w-full text-left px-4 py-3 border transition font-body text-sm ${
                              chosenIsCorrect ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                                : chosenIsWrong ? "border-red-800 bg-red-950/30 text-neutral-300"
                                : chosen ? "border-[#D4AF37] bg-[#D4AF37]/10"
                                : showResult ? "border-[#222] text-neutral-500"
                                : "border-[#222] hover:border-neutral-500 text-neutral-300"
                            }`}>
                            <span className="font-mono text-[10px] text-neutral-500 mr-3">{String.fromCharCode(65 + j)}</span>{o}
                            {chosenIsCorrect && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#D4AF37]">◇ Your answer</span>}
                            {chosenIsWrong && <span className="ml-2 text-[10px] uppercase tracking-widest text-red-400">Your answer</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}

                {!quiz.result && (
                  <button onClick={submitQuiz} disabled={quiz.answers.some(a => a === -1)}
                    className="btn-gold" data-testid="submit-quiz-btn">Submit Quiz</button>
                )}
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
}
